export interface PublicEnvironment {
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_PUBLIC_APP_URL?: string;
  readonly VITE_API_TIMEOUT_MS?: string;
  readonly VITE_TURNSTILE_SITE_KEY?: string;
  readonly VITE_META_PIXEL_ID?: string;
  readonly VITE_GA4_MEASUREMENT_ID?: string;
  readonly VITE_CHECKOUT_PLAN_ID?: string;
  readonly VITE_CHECKOUT_URL?: string;
  readonly VITE_ANALYTICS_CONSENT_REQUIRED?: string;
  readonly VITE_MARKETING_CONSENT_REQUIRED?: string;
  readonly VITE_CONSENT_POLICY_VERSION?: string;
  readonly VITE_TRACKING_DEBUG?: string;
}

export interface AppConfig {
  readonly apiBaseUrl: string;
  readonly publicAppUrl: string;
  readonly apiTimeoutMs: number;
  readonly consentPolicyVersion: string;
  readonly analyticsConsentRequired: boolean;
  readonly marketingConsentRequired: boolean;
  readonly trackingDebug: boolean;
  readonly turnstileSiteKey?: string;
  readonly metaPixelId?: string;
  readonly ga4MeasurementId?: string;
  readonly checkoutPlanId?: string;
  readonly checkoutUrl?: string;
}

export class ConfigurationError extends Error {
  readonly key: keyof PublicEnvironment;

  constructor(key: keyof PublicEnvironment, message: string) {
    super(`Invalid public configuration ${key}: ${message}`);
    this.name = "ConfigurationError";
    this.key = key;
  }
}

const DEFAULT_API_BASE_URL = "/api/v1";
const DEFAULT_API_TIMEOUT_MS = 10_000;
const DEFAULT_POLICY_VERSION = "2026-07-10";
const PLACEHOLDER_PATTERN = /^(?:replace|change|your|example|todo)[-_\s]/i;

function cleanOptional(value: string | undefined, maximumLength = 512): string | undefined {
  if (typeof value !== "string") return undefined;
  const cleaned = value.trim();
  if (!cleaned || cleaned.length > maximumLength || PLACEHOLDER_PATTERN.test(cleaned)) return undefined;
  return cleaned;
}

function parseBoolean(
  key: keyof PublicEnvironment,
  value: string | undefined,
  defaultValue: boolean,
): boolean {
  const cleaned = cleanOptional(value, 16)?.toLowerCase();
  if (cleaned === undefined) return defaultValue;
  if (cleaned === "true") return true;
  if (cleaned === "false") return false;
  throw new ConfigurationError(key, "expected true or false");
}

function parseTimeout(value: string | undefined): number {
  const cleaned = cleanOptional(value, 16);
  if (!cleaned) return DEFAULT_API_TIMEOUT_MS;
  const parsed = Number(cleaned);
  if (!Number.isInteger(parsed) || parsed < 1_000 || parsed > 60_000) {
    throw new ConfigurationError("VITE_API_TIMEOUT_MS", "expected an integer between 1000 and 60000");
  }
  return parsed;
}

function normalizeApiBase(value: string | undefined): string {
  const configured = cleanOptional(value) ?? DEFAULT_API_BASE_URL;
  if (configured.startsWith("/")) {
    const normalized = `/${configured.split("/").filter(Boolean).join("/")}`;
    if (!normalized.endsWith("/api/v1") && normalized !== "/api/v1") {
      throw new ConfigurationError("VITE_API_BASE_URL", "path must end in /api/v1");
    }
    return normalized;
  }

  let url: URL;
  try {
    url = new URL(configured);
  } catch {
    throw new ConfigurationError("VITE_API_BASE_URL", "expected a same-origin path or an absolute URL");
  }
  if (!/^https?:$/.test(url.protocol) || url.username || url.password) {
    throw new ConfigurationError("VITE_API_BASE_URL", "only credential-free HTTP(S) URLs are supported");
  }
  url.hash = "";
  url.search = "";
  url.pathname = url.pathname.replace(/\/+$/, "");
  if (!url.pathname.endsWith("/api/v1")) {
    throw new ConfigurationError("VITE_API_BASE_URL", "URL path must end in /api/v1");
  }
  return url.toString().replace(/\/$/, "");
}

function fallbackOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return "http://localhost";
}

function normalizeWebUrl(
  key: "VITE_PUBLIC_APP_URL" | "VITE_CHECKOUT_URL",
  value: string | undefined,
  fallback?: string,
): string | undefined {
  const configured = cleanOptional(value) ?? fallback;
  if (!configured) return undefined;
  try {
    const url = new URL(configured);
    if (!/^https?:$/.test(url.protocol) || url.username || url.password) throw new Error("unsafe URL");
    url.hash = "";
    return url.toString().replace(/\/$/, "");
  } catch {
    throw new ConfigurationError(key, "expected a credential-free HTTP(S) URL");
  }
}

function validateIdentifier(
  key: "VITE_TURNSTILE_SITE_KEY" | "VITE_CHECKOUT_PLAN_ID",
  value: string | undefined,
): string | undefined {
  const cleaned = cleanOptional(value, 180);
  if (!cleaned) return undefined;
  if (!/^[A-Za-z0-9._-]+$/.test(cleaned)) {
    throw new ConfigurationError(key, "contains unsupported characters");
  }
  return cleaned;
}

function validateMetaPixelId(value: string | undefined): string | undefined {
  const cleaned = cleanOptional(value, 32);
  if (!cleaned) return undefined;
  if (!/^\d{5,32}$/.test(cleaned)) {
    throw new ConfigurationError("VITE_META_PIXEL_ID", "expected a numeric Pixel ID");
  }
  return cleaned;
}

function validateGa4MeasurementId(value: string | undefined): string | undefined {
  const cleaned = cleanOptional(value, 32)?.toUpperCase();
  if (!cleaned) return undefined;
  if (!/^G-[A-Z0-9]{4,30}$/.test(cleaned)) {
    throw new ConfigurationError("VITE_GA4_MEASUREMENT_ID", "expected a GA4 measurement ID beginning with G-");
  }
  return cleaned;
}

export function parsePublicConfig(environment: PublicEnvironment = {}): AppConfig {
  const publicAppUrl = normalizeWebUrl("VITE_PUBLIC_APP_URL", environment.VITE_PUBLIC_APP_URL, fallbackOrigin());
  if (!publicAppUrl) {
    throw new ConfigurationError("VITE_PUBLIC_APP_URL", "could not determine the application origin");
  }

  const turnstileSiteKey = validateIdentifier("VITE_TURNSTILE_SITE_KEY", environment.VITE_TURNSTILE_SITE_KEY);
  const metaPixelId = validateMetaPixelId(environment.VITE_META_PIXEL_ID);
  const ga4MeasurementId = validateGa4MeasurementId(environment.VITE_GA4_MEASUREMENT_ID);
  const checkoutPlanId = validateIdentifier("VITE_CHECKOUT_PLAN_ID", environment.VITE_CHECKOUT_PLAN_ID);
  const checkoutUrl = normalizeWebUrl("VITE_CHECKOUT_URL", environment.VITE_CHECKOUT_URL);
  const policyVersion = cleanOptional(environment.VITE_CONSENT_POLICY_VERSION, 32) ?? DEFAULT_POLICY_VERSION;

  return {
    apiBaseUrl: normalizeApiBase(environment.VITE_API_BASE_URL),
    publicAppUrl,
    apiTimeoutMs: parseTimeout(environment.VITE_API_TIMEOUT_MS),
    consentPolicyVersion: policyVersion,
    analyticsConsentRequired: parseBoolean(
      "VITE_ANALYTICS_CONSENT_REQUIRED",
      environment.VITE_ANALYTICS_CONSENT_REQUIRED,
      true,
    ),
    marketingConsentRequired: parseBoolean(
      "VITE_MARKETING_CONSENT_REQUIRED",
      environment.VITE_MARKETING_CONSENT_REQUIRED,
      true,
    ),
    trackingDebug: parseBoolean("VITE_TRACKING_DEBUG", environment.VITE_TRACKING_DEBUG, false),
    ...(turnstileSiteKey ? { turnstileSiteKey } : {}),
    ...(metaPixelId ? { metaPixelId } : {}),
    ...(ga4MeasurementId ? { ga4MeasurementId } : {}),
    ...(checkoutPlanId ? { checkoutPlanId } : {}),
    ...(checkoutUrl ? { checkoutUrl } : {}),
  };
}

export const appConfig = parsePublicConfig(import.meta.env as unknown as PublicEnvironment);
